import { adminBasePath } from './base.js';

export interface UsageAccount {
  wechatRecordId: number;
  wechatId: string;
  nickname: string | null;
  wechatAlias?: string | null;
  quotaUnits: number;
  usedUnits: number;
  reservedUnits: number;
  remainingUnits: number;
  usagePercent: number;
  sandboxCreated?: number;
  estimatedCostCny: number | null;
}

export interface UsageSummary {
  month: string;
  scope: 'admin' | 'customer';
  totals: {
    quotaUnits: number;
    usedUnits: number;
    reservedUnits: number;
    remainingUnits: number;
    sandboxCreated: number;
    estimatedCostCny: number | null;
  };
  accounts: UsageAccount[];
  costConfigured: boolean;
  kimiPlans: KimiPlanStatus[];
}

export interface KimiPlanStatus {
  planId: string;
  name: string;
  owner?: string;
  state: 'healthy' | 'missing' | 'stale' | 'cooldown' | 'low_quota' | 'saturated';
  snapshot?: {
    observedAt: string;
    weekly: { used: number; limit: number; resetAt: string };
    fiveHour: { used: number; limit: number; resetAt: string };
    parallelLimit: number;
  };
  activeLeases: number;
  capacity: number;
}

export interface OnboardingCheck {
  key: string;
  label: string;
  mode: 'automatic' | 'manual';
  status: 'passed' | 'blocked' | 'pending';
  detail: string;
}

export interface OnboardingDetail {
  account: {
    wechatRecordId: number;
    wechatId: string;
    nickname: string | null;
    capabilities: Record<string, boolean>;
    activatedAt: string | null;
    activatedBy: string | null;
  };
  checks: OnboardingCheck[];
  passedCount: number;
  requiredCount: number;
  ready: boolean;
  handoffAutoProvision: { enabled: boolean; configured: boolean; operatorCount: number };
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${adminBasePath()}/api${path}`, {
    headers: { Accept: 'application/json', ...(options?.body ? { 'Content-Type': 'application/json' } : {}), ...options?.headers },
    ...options,
  });
  const data = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) throw new Error(typeof data.error === 'string' ? data.error : `HTTP ${response.status}`);
  return data as T;
}

export const getUsageSummary = () => request<UsageSummary>('/usage/summary');
export const updateUsageQuota = (wechatRecordId: number, quotaUnits: number) => request(`/usage/accounts/${wechatRecordId}/quota`, {
  method: 'PATCH', body: JSON.stringify({ quotaUnits }),
});
export const createCustomer = (input: { username: string; password: string; displayName?: string; wechatRecordIds: number[] }) => request('/usage/customers', {
  method: 'POST', body: JSON.stringify(input),
});
export const listOnboarding = async () => (await request<{ accounts: OnboardingDetail[] }>('/onboarding')).accounts;
export const updateCapabilities = (wechatRecordId: number, capabilities: Record<string, boolean>) => request<OnboardingDetail>(`/onboarding/${wechatRecordId}/capabilities`, {
  method: 'PUT', body: JSON.stringify({ capabilities }),
});
export const attestOnboarding = (wechatRecordId: number, checkKey: string, passed: boolean) => request<OnboardingDetail>(`/onboarding/${wechatRecordId}/attestations/${checkKey}`, {
  method: 'PUT', body: JSON.stringify({ passed }),
});
export const activateOnboarding = (wechatRecordId: number) => request<OnboardingDetail>(`/onboarding/${wechatRecordId}/activate`, { method: 'POST' });
export const deactivateOnboarding = (wechatRecordId: number) => request<OnboardingDetail>(`/onboarding/${wechatRecordId}/deactivate`, { method: 'POST' });
export const reconcileHandoffGroup = (wechatRecordId: number) => request<{ status: string }>(`/onboarding/${wechatRecordId}/handoff-group/reconcile`, { method: 'POST' });
export const verifyHandoffGroup = (wechatRecordId: number) => request<{ valid: boolean; reason?: string }>(`/onboarding/${wechatRecordId}/handoff-group/verify`, { method: 'POST' });
